<template>
  <nav :class="{ '--active': menuIsVisible, 'sticky-navbar': lang === 'ru' }" class="navbar" id="navbar-section">
    <div class="navbar-content">
      <a class="navbar-logo" :href="href">
        <img src="@/assets/img/logo.svg" alt="logo" />
        <span v-text="translations[lang].name" />
      </a>
      <div :class="[menuIsVisible ? 'navbar-menu navbar-menu--active' : 'navbar-menu', authorized ? '--align-left' : '']">
        <component
          v-for="(item) in filteredNavigation"
          :is="typeof item.link !== 'object' ? 'a' : 'router-link'"
          :key="item.id"
          :to="item.link"
          :href="item.link"
          :class="['navbar-menu__item anchor-link', item.id === 3 || item.id === 4 || item.id === 5 ? 'custom-submenu-item' : '', { 'anchor-link__hide-desktop': item.showOnlyMobile }]"
          @click="item.action ? this[item.action]() : null">
          {{ item.text }}
          <img v-if="item.id === 2 && !showAdditionalMenu" src="@/assets/img/for-companies/menu-arrow-bottom.svg" alt="arrow" @click.stop="toggleSubMenu" />
          <img v-if="item.id === 2 && showAdditionalMenu" src="@/assets/img/for-companies/menu-arrow-top.svg" alt="arrow" @click.stop="toggleSubMenu" />

          <ul v-if="item.id === 2 && showAdditionalMenu && isDesktop" class="navbar-submenu">
            <li v-for="(submenuItem, idx) in item.submenu" :key="idx" class="navbar-submenu__item">
              <a :href="submenuItem.link">{{ submenuItem.text }}</a>
            </li>
          </ul>
        </component>
      </div>

      <button v-if="!authorized && lang === 'en'" class="navbar-menu--button btn-header-sign" @click="handleSignClick">
        <p>Sign up</p>
        <img src="@/assets/img/log-in.svg" alt="log in" />
      </button>
      <button v-if="!authorized && lang === 'en'" class="navbar-menu--button navbar-menu--button-second btn-header-log" @click="handleLoginClick">Log in</button>

      <button v-if="authorized" class="navbar-gfp--button" @click="scrollToShowcase">Upload a floor plan</button>

      <div v-if="authorized" class="navbar-gfp--avatar">
        <img @click="handleAvatarClick" class="navbar-gfp--avatar-icon" src="@/assets/img/avatar.svg" alt="avatar" />
        <p @click="handleAvatarClick" class="navbar-gfp--avatar-name">{{ user?.name ?? '...' }} {{ user?.last_name ?? '' }}</p>
        <img @click="handleSignOut" class="navbar-gfp--avatar-signout" src="@/assets/img/signOut.svg" alt="sign out" />
      </div>

      <button @click="open" :class="!menuIsVisible ? 'navbar-open navbar-open--visible' : 'navbar-open'">
        <div class="navbar-open__top"></div>
        <div class="navbar-open__mid"></div>
        <div class="navbar-open__bot"></div>
      </button>

      <button @click="close" :class="menuIsVisible ? 'navbar-close navbar-close--visible' : 'navbar-close'">
        <div class="navbar-close__left"></div>
        <div class="navbar-close__right"></div>
      </button>
    </div>
  </nav>
</template>

<script>
import { api } from '@/api';
import { smoothScroll } from '@/helpers';
import config from '@/config';
import { getUtmParamsFromStorage, addUtmToUrl } from '@/utils/utm';

export default {
  name: 'Navbar',
  props: {
    isPaymentPage: {
      default: false,
      type: Boolean,
    },
    lang: {
      type: String,
      default: 'en',
    },
  },
  emits: ['user-authorization-status'],
  data() {
    return {
      translations: {
        en: {
          navigation: [
            {
              text: 'About service',
              link: '/',
              id: 0,
            },
            {
              text: 'Pricing',
              link: '/pricing',
              id: 1,
            },
            {
              text: 'For companies',
              link: '#',
              action: 'toggleSubMenu',
              id: 2,
              submenu: [
                { text: 'For Developers ', link: '/developers' },
                { text: 'For Real Estate Management', link: '/real-estate-management' },
                { text: 'For Marketing/Digital Agencies', link: '/marketing' },
              ],
            },
            {
              text: 'For Developers',
              link: '/developers',
              showOnlyOnSubMenu: true,
              id: 3,
              class: 'submenu-item',
            },
            {
              text: 'For Real Estate Management',
              link: '/real-estate-management',
              showOnlyOnSubMenu: true,
              id: 4,
            },
            {
              text: 'For Marketing/Digital Agencies',
              link: '/marketing',
              showOnlyOnSubMenu: true,
              id: 5,
            },
            {
              text: 'For personal users',
              link: '/for-personal-users',
              id: 7,
            },
            {
              text: 'News',
              link: '/news',
              id: 8,
            },
          ],
          name: 'Getfloorplan',
        },
        ru: {
          navigation: [
            {
              text: 'О сервисе',
              link: { path: '/', hash: '#showcase' },
            },
            {
              text: '3D продукты',
              link: { path: '/', hash: '#toolchoose-section' },
            },
            {
              text: 'Как работает',
              link: { path: '/', hash: '#automation-section' },
            },
            {
              text: 'Демо',
              link: { path: '/', hash: '#interior-style-section' },
            },
            {
              text: 'Девелоперам Циан',
              link: `${window.location.origin}/cian`,
            },
            {
              text: 'О компании',
              link: { path: '/company-details' },
            },
            {
              text: 'Примеры материалов',
              link: 'https://backoffice.estate.hart-digital.com/public_plans/528e3ea1-b4fa-4353-b01d-736777a54b36',
              showOnlyMobile: true,
            },
          ],
          name: 'Hart Estate',
        },
      },
      menuIsVisible: false,
      href: window.location.origin,
      authToken: null,
      authorized: false,
      env: config.appEnv,
      refreshInterval: null,
      user: {},
      showAdditionalMenu: false,
      isDesktop: window.innerWidth > 1100,
    };
  },
  computed: {
  filteredNavigation() {
    const { navigation } = this.translations[this.lang];
    const { isDesktop } = this;
    const utmParams = getUtmParamsFromStorage();

    const processedNav = navigation.map((item) => {
      let { link } = item;

      if (typeof link === 'object') {
        link = { ...link, path: addUtmToUrl(link.path || '', utmParams) };
      } else if (typeof link === 'string') {
        const fullLink = link.startsWith('http') ? link : `${window.location.origin}${link}`;
        const newPath = addUtmToUrl(fullLink, utmParams);
        link = newPath.replace(window.location.origin, '');
      }

      return { ...item, link };
    });

    if (this.authorized) {
      return processedNav.filter((item) => [0, 8].includes(item.id));
    }

    return processedNav.filter((item) => {
      if (isDesktop) {
        return !item.showOnlyOnSubMenu;
      }
      if (item.showOnlyOnSubMenu && !this.showAdditionalMenu) {
        return false;
      }
      return true;
    });
  },
},

  mounted() {
    this.authToken = window.localStorage.getItem('token');

    if (this.authToken) {
      this.getAuthUserData();
    } else {
      this.$emit('user-authorization-status', { authorized: false });
    }

    window.addEventListener('scroll', () => {
      const navbar = document.getElementById('navbar-section');
      if (!navbar) return;

      if (window.scrollY > 20) {
        navbar.classList.add('gray-bottom-border');
      } else {
        navbar.classList.remove('gray-bottom-border');
      }
    });

    document.addEventListener('click', this.handleDocumentClick);

    window.addEventListener('resize', this.updateScreenSize);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);

    window.removeEventListener('resize', this.updateScreenSize);
  },
  methods: {
    open() {
      this.menuIsVisible = true;
    },
    close() {
      this.menuIsVisible = false;
    },
    handleLoginClick() {
      if (this.env === 'production') {
        window.location.href = 'https://getfloorplan.com/account/log-in';
      } else {
        window.location.href = 'https://stage.getfloorplan.com/account/log-in';
      }
    },
    handleSignClick() {
      if (this.env === 'production') {
        window.location.href = 'https://getfloorplan.com/account/sign-up';
      } else {
        window.location.href = 'https://stage.getfloorplan.com/account/sign-up';
      }
    },
    handleSignOut() {
      window.localStorage.removeItem('token');
      this.authToken = null;
      this.authorized = false;
      this.user = {};
      window.location.href = '/';
    },
    handleAvatarClick() {
      if (this.env === 'production') {
        window.location.href = 'https://getfloorplan.com/account';
      } else {
        window.location.href = 'https://stage.getfloorplan.com/account';
      }
    },
    async refreshToken() {
      try {
        api.refreshToken(this.authToken);
        window.localStorage.setItem('token', this.authToken);
        await this.getAuthUserData();
      } catch (error) {
        this.handleSignOut();
      }
    },
    async getAuthUserData() {
      try {
        const response = await api.getAuthUserData();
        this.user = response.data;
        this.authorized = true;
        this.$emit('user-authorization-status', {
          authorized: true,
          user: this.user,
        });
      } catch (error) {
        this.handleSignOut();
        this.$emit('user-authorization-status', { authorized: false });
      }
    },
    async scrollToShowcase() {
      if (this.$route.path === '/news') {
        try {
          await this.$router.push('/');
          setTimeout(() => {
            const showcaseSection = document.getElementById('showcase');
            if (showcaseSection) {
              showcaseSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 1000);
        } catch (error) {
          console.error('Ошибка при перенаправлении:', error);
        }
      } else {
        const showcaseSection = document.getElementById('showcase');
        if (showcaseSection) {
          showcaseSection.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },

    scrollTo(el) {
      smoothScroll(el);
    },

    toggleSubMenu() {
      this.showAdditionalMenu = !this.showAdditionalMenu;
    },

    updateScreenSize() {
      this.isDesktop = window.innerWidth > 1100;
    },
  },
};
</script>
